import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Shield, ArrowLeft, Clock, FileText, AlertTriangle, CheckCircle,
  ChevronRight, Trash2, Eye, ArrowUpRight, ArrowDownRight,
  Loader2, Search, Filter, Calendar, TrendingUp, TrendingDown,
  Minus, BarChart3, RefreshCw
} from 'lucide-react';

const getRiskColor = (score) => {
  if (score >= 70) return { bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500/30' };
  if (score >= 40) return { bg: 'bg-orange-500/20', text: 'text-orange-400', border: 'border-orange-500/30' };
  if (score >= 20) return { bg: 'bg-yellow-500/20', text: 'text-yellow-400', border: 'border-yellow-500/30' };
  return { bg: 'bg-green-500/20', text: 'text-green-400', border: 'border-green-500/30' };
};

const getRiskLabel = (score) => {
  if (score >= 70) return 'Alto Riesgo';
  if (score >= 40) return 'Riesgo Moderado';
  if (score >= 20) return 'Riesgo Bajo';
  return 'Aceptable';
};

const AnalysisHistoryPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();

  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedForCompare, setSelectedForCompare] = useState([]);
  const [comparing, setComparing] = useState(false);
  const [comparisonResult, setComparisonResult] = useState(null);
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const response = await api.get('/contract-analysis/history');
      setAnalyses(response.data.analyses || []);
    } catch (error) {
      console.error('Error fetching history:', error);
      toast.error('Error al cargar el historial');
    } finally {
      setLoading(false);
    }
  };

  const deleteAnalysis = async (analysisId) => {
    if (!confirm('¿Seguro que deseas eliminar este análisis?')) return;
    
    try {
      await api.delete(`/contract-analysis/history/${analysisId}`);
      setAnalyses(analyses.filter(a => a.id !== analysisId));
      toast.success('Análisis eliminado');
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const toggleSelect = (analysisId) => {
    if (selectedForCompare.includes(analysisId)) {
      setSelectedForCompare(selectedForCompare.filter(id => id !== analysisId));
    } else if (selectedForCompare.length < 2) {
      setSelectedForCompare([...selectedForCompare, analysisId]);
    } else {
      toast.info('Solo puedes comparar 2 análisis a la vez');
    }
  };

  const compareAnalyses = async () => {
    if (selectedForCompare.length !== 2) {
      toast.error('Selecciona exactamente 2 análisis para comparar');
      return;
    }

    setComparing(true);
    try {
      const response = await api.get(
        `/contract-analysis/compare/${selectedForCompare[0]}/${selectedForCompare[1]}`
      );
      setComparisonResult(response.data);
      setShowCompareModal(true);
    } catch (error) {
      console.error('Error comparing:', error);
      toast.error('Error al comparar análisis');
    } finally {
      setComparing(false);
    }
  };

  const filteredAnalyses = analyses.filter(a => 
    !searchQuery || 
    a.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    a.contract_type?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Fecha desconocida';
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-CL', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen mesh-gradient">
      {/* Header */}
      <header className="glass border-b border-border sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/analizar-contrato" className="flex items-center space-x-3 text-slate-400 hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
              <span className="hidden sm:inline">Analizador</span>
            </Link>
            <div className="flex items-center space-x-3">
              <Clock className="w-6 h-6 text-blue-500" />
              <span className="font-semibold font-['Outfit']">Historial de Análisis</span>
            </div>
            <button
              onClick={fetchHistory}
              className="p-2 text-slate-400 hover:text-white transition-colors"
              title="Refrescar"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Search and Actions */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              placeholder="Buscar análisis..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-slate-800/50 border border-slate-700 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-blue-500"
            />
          </div>
          
          {selectedForCompare.length === 2 && (
            <button
              onClick={compareAnalyses}
              disabled={comparing}
              className="px-6 py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-semibold flex items-center gap-2 disabled:opacity-50"
            >
              {comparing ? <Loader2 className="w-4 h-4 animate-spin" /> : <BarChart3 className="w-4 h-4" />}
              Comparar ({selectedForCompare.length}/2)
            </button>
          )}
        </div>

        {/* Selection Info */}
        {selectedForCompare.length > 0 && (
          <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-3 mb-6 flex items-center justify-between">
            <span className="text-purple-300 text-sm">
              {selectedForCompare.length === 1 
                ? 'Selecciona otro análisis para comparar' 
                : '2 análisis seleccionados para comparación'}
            </span>
            <button
              onClick={() => setSelectedForCompare([])}
              className="text-purple-400 hover:text-purple-300 text-sm"
            >
              Cancelar
            </button>
          </div>
        )}

        {loading ? (
          <div className="text-center py-20">
            <Loader2 className="w-10 h-10 animate-spin mx-auto text-blue-500 mb-4" />
            <p className="text-slate-400">Cargando historial...</p>
          </div>
        ) : filteredAnalyses.length === 0 ? (
          <div className="text-center py-20">
            <FileText className="w-12 h-12 mx-auto text-slate-600 mb-4" />
            <h3 className="text-xl font-semibold mb-2">No hay análisis guardados</h3>
            <p className="text-slate-400 mb-6">
              Analiza un contrato y guarda el resultado para verlo aquí
            </p>
            <Link
              to="/analizar-contrato"
              className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold"
            >
              <Search className="w-5 h-5" />
              Analizar Contrato
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredAnalyses.map((analysis) => {
              const riskColor = getRiskColor(analysis.risk_score || 0);
              const isSelected = selectedForCompare.includes(analysis.id);
              
              return (
                <motion.div
                  key={analysis.id}
                  layout
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`bg-slate-800/50 rounded-xl border transition-all ${
                    isSelected ? 'border-purple-500 ring-1 ring-purple-500/50' : 'border-slate-700'
                  }`}
                >
                  <div className="p-4 sm:p-5">
                    <div className="flex items-start gap-4">
                      {/* Checkbox for comparison */}
                      <button
                        onClick={() => toggleSelect(analysis.id)}
                        className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 mt-1 transition-colors ${
                          isSelected 
                            ? 'bg-purple-500 border-purple-500' 
                            : 'border-slate-500 hover:border-purple-400'
                        }`}
                      >
                        {isSelected && <CheckCircle className="w-3 h-3 text-white" />}
                      </button>

                      {/* Risk Score */}
                      <div className={`w-14 h-14 rounded-xl flex flex-col items-center justify-center flex-shrink-0 ${riskColor.bg}`}>
                        <span className={`text-lg font-bold ${riskColor.text}`}>
                          {analysis.risk_score || 0}
                        </span>
                        <span className="text-[10px] text-slate-400">riesgo</span>
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <h3 className="font-semibold line-clamp-1">
                              {analysis.name || 'Análisis sin nombre'}
                            </h3>
                            <div className="flex flex-wrap items-center gap-2 mt-1">
                              <span className="text-xs px-2 py-0.5 bg-slate-700 rounded text-slate-300">
                                {analysis.contract_type || 'General'}
                              </span>
                              <span className={`text-xs px-2 py-0.5 rounded ${riskColor.bg} ${riskColor.text}`}>
                                {getRiskLabel(analysis.risk_score || 0)}
                              </span>
                              <span className="text-xs text-slate-500 flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {formatDate(analysis.analyzed_at)}
                              </span>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <button
                              onClick={() => deleteAnalysis(analysis.id)}
                              className="p-2 text-slate-400 hover:text-red-400 transition-colors"
                              title="Eliminar"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>

                        {/* Stats */}
                        <div className="flex items-center gap-4 mt-3 text-sm">
                          <span className="text-slate-400">
                            {analysis.total_suggestions || 0} sugerencias
                          </span>
                          <span className="text-slate-500">•</span>
                          <span className="text-slate-400">
                            {analysis.content_length?.toLocaleString() || 0} caracteres
                          </span>
                        </div>

                        {/* Preview */}
                        {analysis.content_preview && (
                          <p className="text-sm text-slate-500 mt-2 line-clamp-2">
                            {analysis.content_preview}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </main>

      {/* Comparison Modal */}
      <AnimatePresence>
        {showCompareModal && comparisonResult && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
            onClick={() => setShowCompareModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-slate-900 border border-slate-700 rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
                  <BarChart3 className="w-6 h-6 text-purple-400" />
                  Comparación de Análisis
                </h2>

                {/* Score Comparison */}
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="text-center p-4 bg-slate-800 rounded-xl">
                    <div className="text-sm text-slate-400 mb-1">Antes</div>
                    <div className={`text-3xl font-bold ${getRiskColor(comparisonResult.analysis_1.risk_score).text}`}>
                      {comparisonResult.analysis_1.risk_score}
                    </div>
                  </div>
                  <div className="text-center p-4 bg-slate-800 rounded-xl flex flex-col items-center justify-center">
                    {comparisonResult.comparison.score_change < 0 ? (
                      <>
                        <TrendingDown className="w-8 h-8 text-green-400" />
                        <div className="text-green-400 font-bold">
                          {Math.abs(comparisonResult.comparison.score_change)} pts
                        </div>
                      </>
                    ) : comparisonResult.comparison.score_change > 0 ? (
                      <>
                        <TrendingUp className="w-8 h-8 text-red-400" />
                        <div className="text-red-400 font-bold">
                          +{comparisonResult.comparison.score_change} pts
                        </div>
                      </>
                    ) : (
                      <>
                        <Minus className="w-8 h-8 text-slate-400" />
                        <div className="text-slate-400">Sin cambio</div>
                      </>
                    )}
                  </div>
                  <div className="text-center p-4 bg-slate-800 rounded-xl">
                    <div className="text-sm text-slate-400 mb-1">Después</div>
                    <div className={`text-3xl font-bold ${getRiskColor(comparisonResult.analysis_2.risk_score).text}`}>
                      {comparisonResult.analysis_2.risk_score}
                    </div>
                  </div>
                </div>

                {/* Improvement Badge */}
                {comparisonResult.comparison.improvement_percentage !== 0 && (
                  <div className={`text-center p-4 rounded-xl mb-6 ${
                    comparisonResult.comparison.improvement_percentage > 0 
                      ? 'bg-green-500/10 border border-green-500/30' 
                      : 'bg-red-500/10 border border-red-500/30'
                  }`}>
                    <span className={`text-2xl font-bold ${
                      comparisonResult.comparison.improvement_percentage > 0 
                        ? 'text-green-400' 
                        : 'text-red-400'
                    }`}>
                      {comparisonResult.comparison.improvement_percentage > 0 ? '+' : ''}
                      {comparisonResult.comparison.improvement_percentage}% mejora
                    </span>
                    <p className="text-sm text-slate-400 mt-1">
                      {comparisonResult.summary}
                    </p>
                  </div>
                )}

                {/* Issue Stats */}
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-xl text-center">
                    <div className="text-2xl font-bold text-green-400">
                      {comparisonResult.comparison.resolved_count}
                    </div>
                    <div className="text-sm text-green-300">Resueltos</div>
                  </div>
                  <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-xl text-center">
                    <div className="text-2xl font-bold text-yellow-400">
                      {comparisonResult.comparison.persistent_count}
                    </div>
                    <div className="text-sm text-yellow-300">Persistentes</div>
                  </div>
                  <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-xl text-center">
                    <div className="text-2xl font-bold text-red-400">
                      {comparisonResult.comparison.new_count}
                    </div>
                    <div className="text-sm text-red-300">Nuevos</div>
                  </div>
                </div>

                {/* Resolved Issues */}
                {comparisonResult.comparison.resolved_issues.length > 0 && (
                  <div className="mb-4">
                    <h4 className="font-semibold text-green-400 mb-2 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      Problemas Resueltos
                    </h4>
                    <ul className="space-y-1">
                      {comparisonResult.comparison.resolved_issues.map((issue, i) => (
                        <li key={i} className="text-sm text-slate-300 flex items-start gap-2">
                          <span className="text-green-400">✓</span>
                          {issue}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* New Issues */}
                {comparisonResult.comparison.new_issues.length > 0 && (
                  <div className="mb-4">
                    <h4 className="font-semibold text-red-400 mb-2 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      Nuevos Problemas
                    </h4>
                    <ul className="space-y-1">
                      {comparisonResult.comparison.new_issues.map((issue, i) => (
                        <li key={i} className="text-sm text-slate-300 flex items-start gap-2">
                          <span className="text-red-400">!</span>
                          {issue}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Close Button */}
                <button
                  onClick={() => setShowCompareModal(false)}
                  className="w-full py-3 bg-slate-800 hover:bg-slate-700 text-white rounded-xl mt-4"
                >
                  Cerrar
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AnalysisHistoryPage;
